import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const Settings = () => {
    const [deliveryAvailable, setDeliveryAvailable] = useState(false);
    const [ignoreDeliveryState, setIgnoreDeliveryState] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [printerOnlineLatest, setPrinterOnlineLatest] = useState(false);
    const [printerOnlineTime, setPrinterOnlineTime] = useState(null);
    const [currentTime, setCurrentTime] = useState(new Date().toLocaleString());
    const [newPassword, setNewPassword] = useState("");
    const [passwordChangeSuccess, setPasswordChangeSuccess] = useState(null);
    const [passwordChangeError, setPasswordChangeError] = useState(null);
    const [specialOpening, setSpecialOpening] = useState({
        enabled: false,
        note: "",
        startDate: "",
        endDate: "",
    });
    const [specialOpeningSuccess, setSpecialOpeningSuccess] = useState(null);
    const [specialOpeningError, setSpecialOpeningError] = useState(null);
    const [closure, setClosure] = useState({
        enabled: false,
        note: "",
        startDate: "",
        endDate: "",
    });
    const [closureSuccess, setClosureSuccess] = useState(null);
    const [closureError, setClosureError] = useState(null);

    const navigate = useNavigate();

    // Fetch JWT from localStorage
    const getAuthToken = () => localStorage.getItem("token");

    // Axios instance with Authorization header
    const axiosInstance = axios.create({
        baseURL: "/api",
        headers: {
            Authorization: `Bearer ${getAuthToken()}`,
        },
    });

    // Fetch settings from the server
    const fetchSetting = async () => {
        setLoading(true);
        try {
            const [deliveryResponse, printerResponse, ignoreDeliveryState] =
                await Promise.all([
                    axiosInstance.get("/admin/settings/deliveryAvailable"),
                    axiosInstance.get("/admin/settings/latestPrinterOnline"),
                    axiosInstance.get("/admin/settings/ignoreDeliveryState"),
                ]);

            setDeliveryAvailable(deliveryResponse.data?.value || false);
            setIgnoreDeliveryState(ignoreDeliveryState.data?.value || false);
            if (printerResponse.data) {
                setPrinterOnlineLatest(printerResponse.data.value);
                setPrinterOnlineTime(printerResponse.data.time);
            }

            // Sonderöffnung optional laden (darf fehlen, daher eigenes catch).
            const specialOpeningResponse = await axiosInstance
                .get("/admin/settings/specialOpening")
                .catch(() => null);
            if (specialOpeningResponse && specialOpeningResponse.data) {
                const value = specialOpeningResponse.data.value || {};
                setSpecialOpening({
                    enabled: Boolean(value.enabled),
                    note: value.note || "",
                    startDate: value.startDate || "",
                    endDate: value.endDate || "",
                });
            }

            // Betriebsschließung optional laden (darf fehlen, daher eigenes catch).
            const closureResponse = await axiosInstance
                .get("/admin/settings/closure")
                .catch(() => null);
            if (closureResponse && closureResponse.data) {
                const value = closureResponse.data.value || {};
                setClosure({
                    enabled: Boolean(value.enabled),
                    note: value.note || "",
                    startDate: value.startDate || "",
                    endDate: value.endDate || "",
                });
            }
        } catch (err) {
            navigate("/login");
        } finally {
            setLoading(false);
        }
    };

    // Save delivery setting to the server
    const saveSetting = async () => {
        setLoading(true);
        try {
            await axiosInstance.post("/admin/settings", {
                key: "deliveryAvailable",
                value: deliveryAvailable,
            });
            await axiosInstance.post("/admin/settings", {
                key: "ignoreDeliveryState",
                value: ignoreDeliveryState,
            });
            setSuccess("Setting updated successfully");
        } catch (err) {
            setError("Error updating the setting");
        } finally {
            setLoading(false);
        }
    };

    // Sonderöffnung speichern (z. B. letzte Woche vor der Schließung)
    const saveSpecialOpening = async () => {
        setSpecialOpeningSuccess(null);
        setSpecialOpeningError(null);
        try {
            await axiosInstance.post("/admin/settings", {
                key: "specialOpening",
                value: {
                    enabled: specialOpening.enabled,
                    note: specialOpening.note,
                    startDate: specialOpening.startDate,
                    endDate: specialOpening.endDate,
                },
            });
            setSpecialOpeningSuccess("Sonderöffnung gespeichert");
        } catch (err) {
            setSpecialOpeningError("Fehler beim Speichern der Sonderöffnung");
        }
    };

    // Betriebsschließung speichern (z. B. Umzug)
    const saveClosure = async () => {
        setClosureSuccess(null);
        setClosureError(null);
        if (closure.enabled && (!closure.startDate || !closure.endDate)) {
            setClosureError("Bitte Von- und Bis-Datum angeben");
            return;
        }
        try {
            await axiosInstance.post("/admin/settings", {
                key: "closure",
                value: {
                    enabled: closure.enabled,
                    note: closure.note,
                    startDate: closure.startDate,
                    endDate: closure.endDate,
                },
            });
            setClosureSuccess("Betriebsschließung gespeichert");
        } catch (err) {
            setClosureError("Fehler beim Speichern der Betriebsschließung");
        }
    };

    // Periodically fetch printer online status
    useEffect(() => {
        const intervalId = setInterval(() => {
            axiosInstance
                .get("/admin/settings/latestPrinterOnline")
                .then((response) => {
                    if (response.data) {
                        setPrinterOnlineLatest(response.data.value);
                        setPrinterOnlineTime(response.data.time);
                    }
                })
                .catch(() => {
                    setError("Error fetching printer status");
                });

            setCurrentTime(new Date().toLocaleString()); // Update displayed current time
        }, 1000); // Fetch every 5 seconds

        return () => clearInterval(intervalId); // Cleanup interval on unmount
    }, []);

    // Fetch initial settings on component mount
    useEffect(() => {
        const token = getAuthToken();
        if (!token) {
            navigate("/login");
            return;
        }
        fetchSetting();
    }, []);

    const handlePasswordChange = async (e) => {
        e.preventDefault();
        setPasswordChangeSuccess(null);
        setPasswordChangeError(null);
        setLoading(true);
        try {
            await axiosInstance.post("/admin/auth/reset-password", {
                email: prompt("Bitte geben Sie Ihre E-Mail-Adresse ein:"),
                newPassword: newPassword,
            });
            setPasswordChangeSuccess("Passwort erfolgreich geändert.");
            setNewPassword("");
        } catch (err) {
            setPasswordChangeError("Fehler beim Ändern des Passworts.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="max-w-md mx-auto bg-white p-6 rounded-md shadow-md mt-16">
            <div>
                <p className="text-sm text-gray-500 mb-4">
                    Current Time: {currentTime}
                </p>
                <p className="text-sm text-gray-500 mb-4">
                    Latest Printer Online:{" "}
                    {new Date(printerOnlineLatest).toLocaleString()}
                </p>
                {printerOnlineTime && (
                    <p className="text-sm text-gray-500 mb-4">
                        Last Online Time:{" "}
                        {new Date(printerOnlineTime).toLocaleString()}
                    </p>
                )}
            </div>
            <h1 className="text-xl font-bold mb-4">Manage Delivery Setting</h1>

            {loading && <p>Loading...</p>}
            {error && <p className="text-red-500">{error}</p>}
            {success && <p className="text-green-500">{success}</p>}

            <div className="flex items-center space-x-4 mb-4">
                <label
                    htmlFor="deliveryAvailable"
                    className="text-sm font-medium"
                >
                    Delivery Available:
                </label>
                <input
                    type="checkbox"
                    id="deliveryAvailable"
                    checked={deliveryAvailable}
                    onChange={(e) => setDeliveryAvailable(e.target.checked)}
                    className="h-5 w-5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                />
            </div>

            <div className="flex items-center space-x-4 mb-4">
                <label
                    htmlFor="ignoreDeliveryState"
                    className="text-sm font-medium"
                >
                    Ignore Delivery State:
                </label>
                <input
                    type="checkbox"
                    id="ignoreDeliveryState"
                    checked={ignoreDeliveryState}
                    onChange={(e) => setIgnoreDeliveryState(e.target.checked)}
                    className="h-5 w-5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                />
            </div>

            <div className="flex space-x-4">
                <button
                    onClick={saveSetting}
                    className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-700"
                >
                    Save
                </button>
            </div>

            <div className="mt-8 border-t pt-6">
                <h2 className="text-lg font-bold mb-2">
                    Sonderöffnung (z. B. letzte Woche)
                </h2>
                <p className="text-sm text-gray-500 mb-4">
                    Überschreibt für den angegebenen Zeitraum die regulären
                    Öffnungszeiten und schaltet die Bestellung frei.
                </p>
                {specialOpeningSuccess && (
                    <p className="text-green-500">{specialOpeningSuccess}</p>
                )}
                {specialOpeningError && (
                    <p className="text-red-500">{specialOpeningError}</p>
                )}

                <div className="flex items-center space-x-4 mb-4">
                    <label
                        htmlFor="specialOpeningEnabled"
                        className="text-sm font-medium"
                    >
                        Aktiv:
                    </label>
                    <input
                        type="checkbox"
                        id="specialOpeningEnabled"
                        checked={specialOpening.enabled}
                        onChange={(e) =>
                            setSpecialOpening((s) => ({
                                ...s,
                                enabled: e.target.checked,
                            }))
                        }
                        className="h-5 w-5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                    />
                </div>

                <div className="space-y-3 mb-4">
                    <div className="flex items-center space-x-4">
                        <label className="text-sm font-medium w-24">Von:</label>
                        <input
                            type="date"
                            value={specialOpening.startDate}
                            onChange={(e) =>
                                setSpecialOpening((s) => ({
                                    ...s,
                                    startDate: e.target.value,
                                }))
                            }
                            className="border rounded-md p-2 flex-1"
                        />
                    </div>
                    <div className="flex items-center space-x-4">
                        <label className="text-sm font-medium w-24">Bis:</label>
                        <input
                            type="date"
                            value={specialOpening.endDate}
                            onChange={(e) =>
                                setSpecialOpening((s) => ({
                                    ...s,
                                    endDate: e.target.value,
                                }))
                            }
                            className="border rounded-md p-2 flex-1"
                        />
                    </div>
                    <div className="flex items-center space-x-4">
                        <label className="text-sm font-medium w-24">
                            Hinweis:
                        </label>
                        <input
                            type="text"
                            placeholder="z. B. Letzte Woche – wir haben für Sie geöffnet"
                            value={specialOpening.note}
                            onChange={(e) =>
                                setSpecialOpening((s) => ({
                                    ...s,
                                    note: e.target.value,
                                }))
                            }
                            className="border rounded-md p-2 flex-1"
                        />
                    </div>
                </div>

                <button
                    onClick={saveSpecialOpening}
                    className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
                >
                    Sonderöffnung speichern
                </button>
            </div>

            <div className="mt-8 border-t pt-6">
                <h2 className="text-lg font-bold mb-2">
                    Betriebsschließung (z. B. Umzug)
                </h2>
                <p className="text-sm text-gray-500 mb-4">
                    Sperrt im Zeitraum Reservierungen und Bestellungen und
                    zeigt den Hinweis auf der Website an (schon vor Beginn).
                    Hat Vorrang vor der Sonderöffnung.
                </p>
                {closureSuccess && (
                    <p className="text-green-500">{closureSuccess}</p>
                )}
                {closureError && <p className="text-red-500">{closureError}</p>}

                <div className="flex items-center space-x-4 mb-4">
                    <label
                        htmlFor="closureEnabled"
                        className="text-sm font-medium"
                    >
                        Aktiv:
                    </label>
                    <input
                        type="checkbox"
                        id="closureEnabled"
                        checked={closure.enabled}
                        onChange={(e) =>
                            setClosure((s) => ({
                                ...s,
                                enabled: e.target.checked,
                            }))
                        }
                        className="h-5 w-5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                    />
                </div>

                <div className="space-y-3 mb-4">
                    <div className="flex items-center space-x-4">
                        <label className="text-sm font-medium w-24">Von:</label>
                        <input
                            type="date"
                            value={closure.startDate}
                            onChange={(e) =>
                                setClosure((s) => ({
                                    ...s,
                                    startDate: e.target.value,
                                }))
                            }
                            className="border rounded-md p-2 flex-1"
                        />
                    </div>
                    <div className="flex items-center space-x-4">
                        <label className="text-sm font-medium w-24">
                            Bis (inkl.):
                        </label>
                        <input
                            type="date"
                            value={closure.endDate}
                            onChange={(e) =>
                                setClosure((s) => ({
                                    ...s,
                                    endDate: e.target.value,
                                }))
                            }
                            className="border rounded-md p-2 flex-1"
                        />
                    </div>
                    <div className="flex items-center space-x-4">
                        <label className="text-sm font-medium w-24">
                            Hinweis:
                        </label>
                        <textarea
                            rows={3}
                            placeholder="z. B. Wegen Umzug vom 12.10. bis 31.10. geschlossen."
                            value={closure.note}
                            onChange={(e) =>
                                setClosure((s) => ({
                                    ...s,
                                    note: e.target.value,
                                }))
                            }
                            className="border rounded-md p-2 flex-1"
                        />
                    </div>
                </div>

                <button
                    onClick={saveClosure}
                    className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
                >
                    Betriebsschließung speichern
                </button>
            </div>

            <div className="mt-8 border-t pt-6">
                <h2 className="text-lg font-bold mb-2">
                    Neues Passwort setzen
                </h2>
                <form
                    onSubmit={handlePasswordChange}
                    className="flex flex-col gap-2"
                >
                    <input
                        type="password"
                        placeholder="Neues Passwort"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="border rounded-md p-2"
                        required
                    />
                    <button
                        type="submit"
                        className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-700"
                        disabled={loading}
                    >
                        Passwort ändern
                    </button>
                    {passwordChangeSuccess && (
                        <div className="text-green-500">
                            {passwordChangeSuccess}
                        </div>
                    )}
                    {passwordChangeError && (
                        <div className="text-red-500">
                            {passwordChangeError}
                        </div>
                    )}
                </form>
            </div>
        </div>
    );
};

export default Settings;
